import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, RouterLink } from '@angular/router';

import { DemandStatus } from '../../../../../../Enums/demand-status.enum';
import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { AvAvatarComponent } from '../../../../../Shared/Components/av-avatar/av-avatar.component';
import { AvContentContainerComponent } from '../../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvEmptyStateComponent } from '../../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvRailComponent } from '../../../../../Shared/Components/av-rail/av-rail.component';
import { AvReferenceComponent } from '../../../../../Shared/Components/av-reference/av-reference.component';
import { AvSkeletonComponent } from '../../../../../Shared/Components/av-skeleton/av-skeleton.component';
import { AvSpinComponent } from '../../../../../Shared/Components/av-spin/av-spin.component';
import { AvAppIconDirective } from '../../../../../Shared/Directives/av-app-icon.directive';
import { AvPendingDirective } from '../../../../../Shared/Directives/av-pending.directive';
import { BreadcrumbService } from '../../../../../Shared/Services/breadcrumb.service';
import { ThemeService } from '../../../../../Shared/Services/theme.service';
import { TranslationService } from '../../../../../Shared/Services/translation.service';
import { waitState } from '../../../../../Shared/Services/wait-state.util';
import { SHORT_ID_LENGTH } from '../../../../../Catalog/Consts/short-id.const';
import { ApprovalStepsStepperComponent } from '../../../../../Catalog/Components/Partials/approval-steps-stepper/approval-steps-stepper.component';
import { DemandActivityLogComponent } from '../../../../../Catalog/Components/Partials/demand-activity-log/demand-activity-log.component';
import { DemandStatusChipComponent } from '../../../../../Catalog/Components/Partials/demand-status-chip/demand-status-chip.component';
import {
  gradientFromSeed,
  iconColorFromSeed,
} from '../../../../../Catalog/Shared/icon-palette.util';
import { resolveIconUrl } from '../../../../../Catalog/Shared/icon-url.util';
import { ALL_DEMANDS_LIST_ROUTE } from '../../../Consts/all-demands-display.const';
import { AllDemandDetailFacade } from '../../../Facades/all-demand-detail.facade';
import { ReportSubjectTab } from '../../../Types/report-subject-tab.type';
import { formatReportPersonName } from '../../../Shared/report-person-name.util';

@Component({
  selector: 'app-all-demand-detail',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    RouterLink,
    AvAvatarComponent,
    AvContentContainerComponent,
    AvEmptyStateComponent,
    AvRailComponent,
    AvReferenceComponent,
    AvSkeletonComponent,
    AvSpinComponent,
    AvPendingDirective,
    AvAppIconDirective,
    ApprovalStepsStepperComponent,
    DemandActivityLogComponent,
    DemandStatusChipComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './all-demand-detail.component.html',
  styleUrl: './all-demand-detail.component.scss',
})
export class AllDemandDetailComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _themeService = inject(ThemeService);
  private readonly _breadcrumb = inject(BreadcrumbService);
  private readonly _translation = inject(TranslationService);
  private readonly _destroyRef = inject(DestroyRef);
  readonly facade = inject(AllDemandDetailFacade);

  readonly listRoute = ALL_DEMANDS_LIST_ROUTE;
  readonly invalidRoute = signal(false);

  private readonly _coldLoading = computed(() => this.facade.loading() && !this.facade.demand());

  protected readonly coldWait = waitState(this._coldLoading, { once: true });

  private readonly _refetching = computed(() => this.facade.loading() && !!this.facade.demand());

  protected readonly refetchWait = waitState(this._refetching);

  protected readonly retryWait = waitState(
    computed(() => this.facade.loading() && this.facade.loadFailed()),
  );

  protected readonly skeletonStepRows: readonly number[] = [1, 2, 3];
  protected readonly skeletonActivityRows: readonly number[] = [1, 2, 3, 4];

  readonly isDark = computed(() => this._themeService.mode() === 'dark');

  readonly isApplication = computed(() => this.facade.demand()?.subject_type === 'application');

  readonly subjectName = computed<string>(() => {
    const demand = this.facade.demand();
    if (!demand) return '';
    const name =
      demand.subject_type === 'application' ? demand.application?.name : demand.group?.name;
    if (name && name.trim().length > 0) return name;
    return this._translation.instant(
      demand.subject_type === 'application'
        ? 'app.catalog.myDemands.subjectFallbackDeletedApplication'
        : 'app.catalog.myDemands.subjectFallbackDeletedGroup',
    );
  });

  readonly subjectDeleted = computed<boolean>(() => {
    const demand = this.facade.demand();
    if (!demand) return false;
    if (demand.subject_deleted !== undefined) return demand.subject_deleted;
    return demand.subject_type === 'application' ? !demand.application : !demand.group;
  });

  readonly kindKey = computed<string>(() =>
    this.isApplication()
      ? 'app.reports.allDemands.detail.subjectKind.application'
      : 'app.reports.allDemands.detail.subjectKind.group',
  );

  readonly iconUrl = computed<string>(() => {
    const demand = this.facade.demand();
    return demand?.subject_type === 'application'
      ? resolveIconUrl(demand.application?.icon_url)
      : '';
  });

  readonly gradient = computed<string>(() => {
    const demand = this.facade.demand();
    if (!demand) return '';
    const seed =
      demand.subject_type === 'application' ? demand.application?.id : demand.group?.name;
    return gradientFromSeed(seed ?? demand.id, this.isDark());
  });

  readonly groupIcon = computed<string>(() => {
    const demand = this.facade.demand();
    return demand?.subject_type === 'group' && demand.group?.type === 'm365' ? 'groups' : 'shield';
  });

  readonly groupIconColor = computed<string | null>(() => {
    const demand = this.facade.demand();
    if (!demand || demand.subject_type !== 'group') return null;
    return iconColorFromSeed(demand.group?.name ?? demand.id, this.isDark());
  });

  readonly status = computed<DemandStatus | null>(() => this.facade.demand()?.status?.name ?? null);

  readonly requesterName = computed(() => formatReportPersonName(this.facade.demand()?.created_by));

  readonly moderatorName = computed(() => {
    const demand = this.facade.demand();
    if (!demand) return '';
    const moderator =
      demand.subject_type === 'application'
        ? demand.application?.moderator
        : demand.group?.moderator;
    return formatReportPersonName(moderator);
  });

  readonly deviceLabel = computed<string>(() => {
    const demand = this.facade.demand();
    if (!demand || demand.subject_type !== 'application') return '';
    const name = demand.device_name?.trim();
    if (name) return name;
    const id = demand.device_intune_id;
    return id ? `…${id.slice(-SHORT_ID_LENGTH)}` : '';
  });

  readonly hasChain = computed(() => (this.facade.demand()?.approvals.length ?? 0) > 0);

  constructor() {
    this._bindRouteParams();
    this._registerBreadcrumbSync();
    this._destroyRef.onDestroy(() => this._leave());
  }

  private _bindRouteParams(): void {
    this._route.paramMap
      .pipe(takeUntilDestroyed())
      .subscribe((params) => this._applyRouteParams(params));
  }

  private _applyRouteParams(params: ParamMap): void {
    const tab = this._resolveTab(params.get('type'));
    const id = params.get('id') ?? '';
    this.invalidRoute.set(tab === null || id === '');
    if (tab && id) {
      this.facade.setRoute(tab, id);
    } else {
      this.facade.reset();
    }
  }

  private _resolveTab(raw: string | null): ReportSubjectTab | null {
    return raw === 'application' || raw === 'group' ? raw : null;
  }

  private _registerBreadcrumbSync(): void {
    effect(() => this._breadcrumb.setDynamicLabel(this.subjectName() || null));
  }

  private _leave(): void {
    this._breadcrumb.setDynamicLabel(null);
    this.facade.reset();
  }
}
